import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

// API/Components
import { STATE_COLOR, isEmpty, formatDateTime } from './enums';

// Material UI
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { Typography } from '@mui/material';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ArrowCircleUpOutlinedIcon from '@mui/icons-material/ArrowCircleUpOutlined';
import ArrowCircleDownOutlinedIcon from '@mui/icons-material/ArrowCircleDownOutlined';

// Table display if empty
const StyledGridOverlay = styled('div')(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	height: '100%',
	'& .ant-empty-img-1': {
		fill: theme.palette.mode === 'light' ? '#aeb8c2' : '#262626',
	},
	'& .ant-empty-img-2': {
		fill: theme.palette.mode === 'light' ? '#f5f5f7' : '#595959',
	},
	'& .ant-empty-img-3': {
		fill: theme.palette.mode === 'light' ? '#dce0e6' : '#434343',
	},
	'& .ant-empty-img-4': {
		fill: theme.palette.mode === 'light' ? '#fff' : '#1c1c1c',
	},
	'& .ant-empty-img-5': {
		fillOpacity: theme.palette.mode === 'light' ? '0.8' : '0.08',
		fill: theme.palette.mode === 'light' ? '#f5f5f5' : '#fff',
	},
}));

function NoDataOverlay() {
	return (
		<StyledGridOverlay>
			<svg
				width="120"
				height="100"
				viewBox="0 0 184 152"
				aria-hidden
				focusable="false"
			>
				<g fill="none" fillRule="evenodd">
					<g transform="translate(24 31.67)">
						<ellipse
							className="ant-empty-img-5"
							cx="67.797"
							cy="106.89"
							rx="67.797"
							ry="12.668"
						/>
						<path
							className="ant-empty-img-1"
							d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
						/>
						<path
							className="ant-empty-img-2"
							d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
						/>
						<path
							className="ant-empty-img-3"
							d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
						/>
					</g>
					<path
						className="ant-empty-img-3"
						d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
					/>
					<g className="ant-empty-img-4" transform="translate(149.65 15.383)">
						<ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
						<path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
					</g>
				</g>
			</svg>
			<Box sx={{ mt: 1 }}>No Discharge/Charge Events</Box>
		</StyledGridOverlay>
	);
}

function buildFormattedCell(value, valueRounding, valueUnits, emptyText) {
	// Format value text
	var valueText = emptyText;

	if (value && value != 0) {
		const valueDecimals = valueRounding? valueRounding : 3;
		valueText = value.toFixed(valueDecimals);
	}

	const cellText = `${valueText} ${valueUnits}`;
	
	return buildCell(cellText, 'center');
}

function buildEventTypeCell(eventTypeCode) {
	
	var eventType = 'Unknown';
	var eventIcon = <HelpOutlineOutlinedIcon sx={{ color: STATE_COLOR.UNKNOWN }}/>;
	
	switch (eventTypeCode) {
		case 0:
			eventType =  'Unknown';
			eventIcon = <HelpOutlineOutlinedIcon sx={{ color: STATE_COLOR.UNKNOWN }}/>;
			break;
		case 1:
			eventType =  'Discharge';
			eventIcon = <ArrowCircleDownOutlinedIcon sx={{ color: STATE_COLOR.CRITICAL }}/>
			break;
		case 2:
			eventType =  'Charge';
			eventIcon = <ArrowCircleUpOutlinedIcon sx={{ color: STATE_COLOR.WITHIN_LIMITS }}/>
			break;
		default:
			eventType =  'Unknown';
			eventIcon = <HelpOutlineOutlinedIcon sx={{ color: STATE_COLOR.UNKNOWN }}/>;
			break;
	}

	return (
		<div
			style={{
				width: '100%',
				height: '100%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			{eventIcon}
			<Typography fontFamily='Saira, sans-serif' sx={{ pl: 1 }}>{eventType}</Typography>
		</div>
	);
}

function buildCell(text, alignment) {
	return (
		<div
			style={{
				width: '100%',
				height: '100%',
				display: 'flex',
				alignItems: alignment,
				justifyContent: alignment,
			}}
		>
			<Typography fontFamily='Saira, sans-serif'>{text}</Typography>
		</div>
	);
}

// System Trend Report table columns
const columns = [
	{
		field: 'start_datetime',
		headerName: 'Event Start Date',
		description: 'Event Start Date and Time',
		minWidth: 170,
		flex: 1,
		headerAlign: "left",
		renderCell: (params) => formatDateTime(params.value),
	},
	{
		field: 'event_type',
		headerName: 'Event Type',
		description: 'Event Type',
		minWidth: 150,
		flex: 1,
		headerAlign: "center",
		renderCell: (params) => buildEventTypeCell(params.value),
	},
	{
		field: 'duration_text',
		headerName: 'Duration',
		description: 'Duration',
		minWidth: 120,
		flex: 1,
		headerAlign: "center",
		renderCell: (params) => buildCell(params.value, 'center')
	},
	{
		field: 'capacity',
		headerName: 'Capacity',
		description: 'Capacity Drained/Replenished',
		minWidth: 120,
		flex: 1,
		headerAlign: "center",
		renderCell: (params) => buildFormattedCell(params.value, 2, 'Ah', '-.-')
	},
	{
		field: 'energy',
		headerName: 'Energy',
		description: 'Average Energy',
		minWidth: 120,
		flex: 1,
		headerAlign: "center",
		renderCell: (params) => buildFormattedCell(params.value, 2, 'KWh', '----.--')
	},
	{
		field: 'power',
		headerName: 'Power',
		description: 'Average Power',
		minWidth: 120,
		flex: 1,
		headerAlign: "center",
		renderCell: (params) => buildFormattedCell(params.value, 2, 'KW', '---.--')
	},
	{
		field: 'note',
		headerName: 'Note',
		description: 'Measurement Notes',
		minWidth: 190,
		flex: 5,
		headerAlign: "left",
	},
];


export default function DischargeEventsList(props) {
	const { locationID, eventsData, loading } = props;
	
	const [isLoading, setIsLoading] = useState(loading);
	const [pageSize, setPageSize] = useState(50);

	const navigate = useNavigate();

	useEffect(() => {
		setIsLoading(loading);
	}, [loading]);

	function convertDateTimeToLinkString(dateTime) {
		return dateTime.replace('-', '').replace('-', '').replace('T', '').replace(':', '').replace(':', '').replace('Z', '');
	}
	  
	return (
		<DataGrid
			getRowId={(row) => convertDateTimeToLinkString(row.start_datetime)}
			rows={eventsData}
			columns={columns}
			loading={isLoading}
			density="compact"
			pageSize={pageSize}
			onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
			rowsPerPageOptions={[25, 50, 100]}  // Max pagination size for DataGrid (Free Edition) is 100
			onRowClick={(rowParams) => {
				const dischargeDateTime = rowParams.id;
				navigate(`/location/${locationID}/discharges/${dischargeDateTime}/`);
			}}
			components={{
				NoRowsOverlay: NoDataOverlay,
			}}
			sx={{
				padding: 1,
				height: '100%'
			}}
		/>
	);
}